import { useSyncExternalStore } from "react";
import { appStore, type AppState } from "../../app/store.ts";
import { BUILD } from "../../env.ts";
import { platform } from "../platform.ts";

/**
 * AdMob in the Google Play app (Capacitor plugin @capacitor-community/admob).
 *
 * The player is never interrupted: no interstitials, no app-open ads. A small
 * adaptive banner sits under the city's nav bar, and rewarded ads play only
 * when the player taps a button that says what they get. Inside a level, on
 * the result card and on every full screen sheet there is no banner.
 *
 * Before anything loads, Google's UMP form asks players in the EEA, UK and
 * Switzerland for consent; Settings reopens it when Google says it must.
 * The web build and sideloaded APKs never import the plugin.
 */

/** Google's sample units: always fill, never pay, safe to tap. */
const TEST_UNITS = {
  banner: "ca-app-pub-3940256099942544/9214589741",
  hint: "ca-app-pub-3940256099942544/5224354917",
  gold: "ca-app-pub-3940256099942544/5224354917",
};

/** NANO Magical City in the owner's AdMob account (pub-8651888855380200). */
const LIVE_UNITS = {
  banner: "ca-app-pub-8651888855380200/BANNER_UNIT",
  /** Rewarded, paid on the device: one more hint. */
  hint: "ca-app-pub-8651888855380200/HINT_UNIT",
  /** Rewarded with server-side verification to /api/v1/ads/ssv: gift chests and doubled gold. */
  gold: "ca-app-pub-8651888855380200/GOLD_UNIT",
};

const units = () => (BUILD.liveAds ? LIVE_UNITS : TEST_UNITS);

export type AdPlacement = "hint" | "gold";
export type WatchResult = "rewarded" | "skipped" | "unavailable";

export interface AdsState {
  /** off: not the Play app. starting: consent and SDK. ready: ads may load. blocked: no consent or no SDK. */
  readonly phase: "off" | "starting" | "ready" | "blocked";
  /** Google says this player must be able to reopen the consent form (EEA, UK, Switzerland). */
  readonly privacyOptions: boolean;
  /** A rewarded ad is loading or playing. */
  readonly busy: boolean;
}

let state: AdsState = { phase: "off", privacyOptions: false, busy: false };
const listeners = new Set<() => void>();

function patch(next: Partial<AdsState>): void {
  state = { ...state, ...next };
  for (const listener of listeners) listener();
}

export function adsState(): AdsState {
  return state;
}

export function useAds(): AdsState {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => state,
  );
}

/** Ads exist in this build at all (the Play app on a phone, or ?ads=1 in a browser to preview the flows). */
export function adsEnabled(): boolean {
  if (fakeAds()) return true;
  return BUILD.store === "play" && platform.native();
}

/** Browser preview of the ad flows: a pretend ad that always rewards after a beat. */
function fakeAds(): boolean {
  return !platform.native() && typeof location !== "undefined" && new URLSearchParams(location.search).get("ads") === "1";
}

const plugin = () => import("@capacitor-community/admob");

/**
 * Once at start-up in the Play app: consent first (Google's form shows only
 * where the law needs it), then the SDK, then the city banner follows the
 * screen. Family friendly ad content only (rated PG at most).
 */
export async function startAds(): Promise<void> {
  if (!adsEnabled() || state.phase !== "off") return;
  if (fakeAds()) {
    patch({ phase: "ready" });
    followScreens();
    return;
  }
  patch({ phase: "starting" });
  try {
    const { AdMob, AdmobConsentStatus, MaxAdContentRating } = await plugin();
    let consent = await AdMob.requestConsentInfo();
    if (consent.isConsentFormAvailable && consent.status === AdmobConsentStatus.REQUIRED) {
      consent = await AdMob.showConsentForm();
    }
    patch({ privacyOptions: String(consent.privacyOptionsRequirementStatus) === "REQUIRED" });
    if (!consent.canRequestAds) {
      patch({ phase: "blocked" });
      return;
    }
    await AdMob.initialize({ maxAdContentRating: MaxAdContentRating.ParentalGuidance });
    patch({ phase: "ready" });
    followScreens();
  } catch (error) {
    console.warn("ads unavailable", error);
    patch({ phase: "blocked" });
  }
}

/** Settings: Google's privacy options form, then ads follow the new choice. */
export async function showAdPrivacyOptions(): Promise<void> {
  if (!adsEnabled() || fakeAds()) return;
  const { AdMob } = await plugin();
  await AdMob.showPrivacyOptionsForm().catch(() => undefined);
  const consent = await AdMob.requestConsentInfo().catch(() => null);
  if (consent && !consent.canRequestAds) {
    await hideBanner();
    patch({ phase: "blocked" });
  } else if (consent?.canRequestAds && state.phase === "blocked") {
    patch({ phase: "off" });
    await startAds();
  }
}

// --- the city banner -------------------------------------------------------------------

/** Only the city map itself: never in a level, on a result, in the picker or under a loading gate. */
export function wantsBanner(app: AppState): boolean {
  const ui = app.ui;
  return ui.screen === "city" && ui.cityReady && !ui.installGuide && ui.preload === null && !ui.contextLost;
}

let bannerShown = false;
let bannerLoaded = false;
let following = false;
let bannerJob: Promise<void> = Promise.resolve();

/** Extra room between the banner and the nav bar, so a thumb aiming at a button never lands on the ad. */
const BANNER_GAP = 8;

/** The nav bar and sheets sit above the banner: its height joins the bottom safe area. */
function setBannerInset(height: number): void {
  const inset = height > 0 ? Math.round(height) + BANNER_GAP : 0;
  document.documentElement.style.setProperty("--ad-bottom", `${inset}px`);
}

async function showBanner(): Promise<void> {
  if (bannerShown || state.phase !== "ready") return;
  bannerShown = true;
  if (fakeAds()) {
    setBannerInset(56);
    document.documentElement.classList.add("fake-ad-banner");
    return;
  }
  const { AdMob, BannerAdPosition, BannerAdSize } = await plugin();
  try {
    if (bannerLoaded) await AdMob.resumeBanner();
    else {
      await AdMob.showBanner({ adId: units().banner, adSize: BannerAdSize.ADAPTIVE_BANNER, position: BannerAdPosition.BOTTOM_CENTER, margin: 0 });
      bannerLoaded = true;
    }
  } catch (error) {
    // No fill or no network: the city simply has no banner this time.
    console.warn("banner failed", error);
    bannerShown = false;
    setBannerInset(0);
  }
}

async function hideBanner(): Promise<void> {
  if (!bannerShown) return;
  bannerShown = false;
  setBannerInset(0);
  if (fakeAds()) {
    document.documentElement.classList.remove("fake-ad-banner");
    return;
  }
  const { AdMob } = await plugin();
  await AdMob.hideBanner().catch(() => undefined);
}

function followScreens(): void {
  if (following) return;
  following = true;
  if (!fakeAds()) {
    void plugin().then(({ AdMob, BannerAdPluginEvents }) => {
      void AdMob.addListener(BannerAdPluginEvents.SizeChanged, (size) => {
        if (bannerShown) setBannerInset(size.height);
      });
      void AdMob.addListener(BannerAdPluginEvents.FailedToLoad, () => setBannerInset(0));
    });
  }
  const sync = () => {
    const want = wantsBanner(appStore.get());
    // One change at a time, in order, so a quick city to level hop cannot leave a banner over the level.
    bannerJob = bannerJob.then(() => (want ? showBanner() : hideBanner())).catch(() => undefined);
  };
  let last = wantsBanner(appStore.get());
  appStore.subscribe(() => {
    const want = wantsBanner(appStore.get());
    if (want === last) return;
    last = want;
    sync();
  });
  sync();
}

// --- rewarded ads ----------------------------------------------------------------------

/**
 * Plays one rewarded ad the player asked for. `ssv` rides along to Google's
 * signed reward callback (who watched, and what the Worker should pay).
 * Resolves when the ad closes: rewarded only if Google granted the reward.
 */
export async function watchRewarded(placement: AdPlacement, ssv?: { userId: string; customData: string }): Promise<WatchResult> {
  if (state.phase !== "ready" || state.busy) return "unavailable";
  patch({ busy: true });
  try {
    if (fakeAds()) {
      await new Promise((resolve) => setTimeout(resolve, 700));
      return "rewarded";
    }
    const { AdMob, RewardAdPluginEvents } = await plugin();
    try {
      await AdMob.prepareRewardVideoAd({ adId: units()[placement], immersiveMode: true, ...(ssv ? { ssv } : {}) });
    } catch {
      return "unavailable";
    }
    return await new Promise<WatchResult>((resolve) => {
      let rewarded = false;
      let settled = false;
      const handles: { remove: () => Promise<void> }[] = [];
      const finish = (result: WatchResult) => {
        if (settled) return;
        settled = true;
        for (const handle of handles) void handle.remove();
        resolve(result);
      };
      void Promise.all([
        AdMob.addListener(RewardAdPluginEvents.Rewarded, () => {
          rewarded = true;
        }),
        AdMob.addListener(RewardAdPluginEvents.Dismissed, () => finish(rewarded ? "rewarded" : "skipped")),
        AdMob.addListener(RewardAdPluginEvents.FailedToShow, () => finish("unavailable")),
      ]).then((added) => {
        handles.push(...added);
        if (settled) for (const handle of added) void handle.remove();
        // Resolves only once the reward is earned; a dismissal without it leaves this pending.
        AdMob.showRewardVideoAd().then(
          () => {
            rewarded = true;
          },
          () => finish("unavailable"),
        );
      });
    });
  } finally {
    patch({ busy: false });
  }
}
